import { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { Loader2, BookOpen, Lightbulb, CheckCircle2, Languages } from "lucide-react";
import { getLearnPage } from "../lib/api";
import { markRead } from "../lib/wellnessProgress";
import { AnswerDisplay } from "../components/AnswerDisplay";
import { QuestionBox } from "../components/QuestionBox";
import { Disclaimer } from "../components/Disclaimer";
import { useT, useLang, SUPPORTED, LANG_LABELS } from "../lib/i18n";

export default function LearnPage() {
  const { slug, lang: routeLang } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const { lang, setLang } = useLang();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Explicit locale from the URL (path /es/slug or legacy ?hl=es). The unprefixed
  // URL is always canonical English, so content language never leaks from a prior
  // locale visit. We only sync the global UI language when a locale is explicit.
  const hlParam = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("hl")
    : null;
  const explicitLang =
    routeLang && SUPPORTED.includes(routeLang)
      ? routeLang
      : hlParam && SUPPORTED.includes(hlParam)
      ? hlParam
      : null;
  const pageLang = explicitLang || "en";

  useEffect(() => {
    // Keep the navbar/shell in sync only when the URL carries an explicit locale.
    if (explicitLang && explicitLang !== lang) setLang(explicitLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explicitLang]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    getLearnPage(slug, pageLang)
      .then((data) => {
        if (!active) return;
        data.answer_lang = pageLang;
        setResult(data);
        document.title = `${data.title} — AskMyDoctor AI`;
        document.documentElement.lang = pageLang;
        if (data.guide) markRead(slug);
        const a = data.answer || {};
        // meta description
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
          meta = document.createElement("meta");
          meta.name = "description";
          document.head.appendChild(meta);
        }
        meta.setAttribute("content", (a.simple_answer || "").slice(0, 300));
        // hreflang alternates + canonical, using per-locale path URLs (/es/slug …)
        document.querySelectorAll("link.learn-hreflang").forEach((el) => el.remove());
        const origin = window.location.origin;
        const urlFor = (l) => (l === "en" ? `${origin}/${slug}` : `${origin}/${l}/${slug}`);
        SUPPORTED.forEach((l) => {
          const link = document.createElement("link");
          link.className = "learn-hreflang";
          link.rel = "alternate";
          link.hreflang = l;
          link.href = urlFor(l);
          document.head.appendChild(link);
        });
        // x-default points to the English page
        const xd = document.createElement("link");
        xd.className = "learn-hreflang";
        xd.rel = "alternate";
        xd.hreflang = "x-default";
        xd.href = urlFor("en");
        document.head.appendChild(xd);
        // canonical for the current language
        let canon = document.querySelector("link.learn-canonical");
        if (!canon) {
          canon = document.createElement("link");
          canon.className = "learn-canonical";
          canon.rel = "canonical";
          document.head.appendChild(canon);
        }
        canon.href = urlFor(pageLang);
        const jsonLd = {
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          inLanguage: pageLang,
          name: data.title,
          description: a.simple_answer || "",
          mainEntity: {
            "@type": "Question",
            name: data.question,
            acceptedAnswer: { "@type": "Answer", text: a.simple_answer || "" },
          },
        };
        let el = document.getElementById("learn-jsonld");
        if (!el) {
          el = document.createElement("script");
          el.type = "application/ld+json";
          el.id = "learn-jsonld";
          document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(jsonLd);
      })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });

    return () => {
      active = false;
      const el = document.getElementById("learn-jsonld");
      if (el) el.remove();
      document.querySelectorAll("link.learn-hreflang").forEach((x) => x.remove());
      document.querySelectorAll("link.learn-canonical").forEach((x) => x.remove());
      document.title = "AskMyDoctor AI";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, pageLang]);

  if (notFound) return <Navigate to="/" replace />;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {loading || !result ? (
        <div className="py-20 text-center">
          <Loader2 className="w-10 h-10 text-teal-600 animate-spin mx-auto" />
          <p className="mt-4 text-lg text-slate-500">{t("learn_loading")}</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 text-teal-700 font-semibold text-sm uppercase tracking-wider mb-2">
            <BookOpen className="w-4 h-4" /> {t("nav_library")}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
            {result.title}
          </h1>

          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="lang-switch-chips">
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500">
              <Languages className="w-4 h-4" /> {t("read_in")}
            </span>
            {SUPPORTED.filter((L) => L !== pageLang).map((L) => (
              <Link
                key={L}
                to={L === "en" ? `/${slug}` : `/${L}/${slug}`}
                data-testid={`read-in-${L}`}
                className="inline-flex items-center rounded-full border-2 border-teal-200 bg-white px-3.5 py-1.5 text-sm font-semibold text-teal-700 hover:border-teal-400 hover:bg-teal-50 transition-colors"
              >
                {LANG_LABELS[L]}
              </Link>
            ))}
          </div>

          <AnswerDisplay result={result} currentSlug={slug} />
          {result.guide && (
            <div className="mt-8 space-y-4" data-testid="wellness-guide">
              <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 p-6">
                <h2 className="font-heading font-bold text-xl text-slate-900 mb-2 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-emerald-600" /> {t("learn_why")}
                </h2>
                <p className="text-lg text-slate-700 leading-relaxed">{result.guide.why}</p>
              </div>
              {result.guide.tips && result.guide.tips.length > 0 && (
                <div className="rounded-2xl border-2 border-slate-200 bg-white p-6">
                  <h2 className="font-heading font-bold text-xl text-slate-900 mb-3">{t("learn_tips")}</h2>
                  <ul className="space-y-2.5">
                    {result.guide.tips.map((tip, k) => (
                      <li key={tip} className="flex items-start gap-2.5 text-lg text-slate-700 leading-relaxed" data-testid={`wellness-tip-${k}`}>
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-1 shrink-0" />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <div className="mt-8 rounded-2xl border-2 border-teal-200 bg-teal-50/40 p-6">
            <h2 className="font-heading font-bold text-xl text-slate-900 mb-4">
              {t("learn_own_q")}
            </h2>
            <QuestionBox
              testIdPrefix="learn"
              onSubmit={(q) => navigate(`/answer?q=${encodeURIComponent(q)}`)}
            />
          </div>
          <div className="mt-6">
            <Disclaimer compact />
          </div>
        </>
      )}
    </div>
  );
}
